// packages/shared-components/src/hooks/useDecorativeLighting.ts

import { useState, useCallback, useEffect, useRef } from "react";
import type {
  KeyboardSDK,
  CustomLightingInfo,
  DecorativeArea,
  GetDecorateCustomParams,
  SetDecorateCustomParams,
} from "../types/sdk";
import {
  isOwnerResyncLoading,
  OWNER_RESYNC_LOADING_CHANGED_EVENT,
} from "../utils/deviceSessionOwnerResync";
import { areDotMatrixGridsEqual } from "../utils/dotMatrixUtils";

export interface UseDecorativeLightingOptions {
  /** 键盘 SDK 实例（兼容 sdkApi 命名） */
  sdkApi?: KeyboardSDK | null;
  sdk?: KeyboardSDK | null;
  /** 装饰灯区域 */
  area: DecorativeArea;
  /** 行数 */
  rows: number;
  /** 列数 */
  cols: number;
  /** 是否启用（默认 true） */
  enabled?: boolean;
  /** 轮询间隔（毫秒，0 表示不轮询） */
  pollingInterval?: number;
}

export interface UseDecorativeLightingResult {
  /** 灯光数据 */
  lightingData: CustomLightingInfo[][] | null;
  /** 是否加载中 */
  isLoading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 加载灯光数据；silent 为 true 时不展示 loading，但仍更新灯色 */
  loadLightingData: (options?: {
    silent?: boolean;
  }) => Promise<CustomLightingInfo[][] | null>;
  /** 更新灯光数据；silent 为 true 时不切换 isLoading，避免整页重渲染卡顿 */
  updateLightingData: (
    data: CustomLightingInfo[][],
    options?: { silent?: boolean }
  ) => Promise<void>;
  /** 更新单个 LED */
  updateSingleLed: (
    row: number,
    col: number,
    color: { R: number; G: number; B: number }
  ) => Promise<void>;
  /** 清空灯光 */
  clearLighting: () => Promise<void>;
  /** 本地更新（不发送到设备） */
  setLocalLighting: (data: CustomLightingInfo[][]) => void;
}

/**
 * 检查响应是否成功
 */
function isSuccessResponse(code: number): boolean {
  // 兼容不同的状态码约定
  // 0: 业务成功码
  // 200: HTTP 成功码
  return code === 0 || code === 200;
}

/**
 * 装饰灯管理 Hook
 */
export function useDecorativeLighting({
  sdkApi,
  sdk,
  area,
  rows,
  cols,
  enabled = true,
  pollingInterval = 0,
}: UseDecorativeLightingOptions): UseDecorativeLightingResult {
  // 兼容两种参数命名
  const actualSdk = sdkApi || sdk;
  const { sdkKeyboardBasicApi } = sdkApi || {};
  // 用于判断华奋达
  const getCurrentEquipmentData = sdkKeyboardBasicApi?.getCurrentEquipmentData;
  const solutionRovider = getCurrentEquipmentData?.()?.solutionRovider;

  const [lightingData, setLightingData] = useState<
    CustomLightingInfo[][] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollingTimeoutRef = useRef<number | null>(null);
  const isFetchingRef = useRef(false);
  const solutionRoviderRef = useRef(solutionRovider);
  solutionRoviderRef.current = solutionRovider;

  /**
   * 加载装饰灯数据
   */
  const loadLightingData = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!actualSdk?.sdkKeyboardLightingApi?.getDecorate1Custom) {
        const errorMsg = "SDK 未初始化或不支持装饰灯 API";
        if (!options?.silent) {
          setError(errorMsg);
          console.warn("[useDecorativeLighting]", errorMsg);
        }
        return null;
      }

      if (!enabled) {
        return null;
      }

      const silent = options?.silent === true;
      if (!silent && !isOwnerResyncLoading()) {
        setIsLoading(true);
      }
      if (!silent) {
        setError(null);
      }

      try {
        const params: GetDecorateCustomParams = {
          rows,
          cols,
          area,
        };

        const response =
          await actualSdk.sdkKeyboardLightingApi.getDecorate1Custom(params);

        if (isSuccessResponse(response.code) && response.data) {
          setLightingData((prev) => {
            if (prev && areDotMatrixGridsEqual(prev, response.data)) {
              return prev;
            }
            return response.data;
          });
          return response.data;
        }
        throw new Error(response.message || "获取装饰灯数据失败");
      } catch (err) {
        if (!silent) {
          const errorMsg =
            err instanceof Error ? err.message : "获取装饰灯数据失败";
          setError(errorMsg);
          console.error("[useDecorativeLighting] 获取装饰灯数据失败:", err);
        }
        return null;
      } finally {
        if (!silent && !isOwnerResyncLoading()) {
          setIsLoading(false);
        }
      }
    },
    [actualSdk, area, rows, cols, enabled]
  );

  const loadLightingDataRef = useRef(loadLightingData);
  loadLightingDataRef.current = loadLightingData;

  /**
   * 更新装饰灯数据
   */
  const updateLightingData = useCallback(
    async (data: CustomLightingInfo[][], options?: { silent?: boolean }) => {
      if (!actualSdk?.sdkKeyboardLightingApi?.setDecorate1Custom) {
        setError("SDK 未初始化或不支持装饰灯 API");
        return;
      }

      const silent = options?.silent === true;
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const params: SetDecorateCustomParams = {
          area,
          protocol: "Custom",
          data,
        };

        const response =
          await actualSdk.sdkKeyboardLightingApi.setDecorate1Custom(params);

        // ✅ 修复：兼容 code: 0 和 code: 200
        if (isSuccessResponse(response.code)) {
          if (!silent) {
            setLightingData(data);
            console.log("[useDecorativeLighting] 数据更新成功");
          }
        } else {
          throw new Error(response.message || "设置装饰灯数据失败");
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "设置装饰灯数据失败";
        setError(errorMsg);
        console.error("[useDecorativeLighting] 设置装饰灯数据失败:", err);
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [actualSdk, area]
  );

  /**
   * 更新单个 LED
   */
  const updateSingleLed = useCallback(
    async (
      row: number,
      col: number,
      color: { R: number; G: number; B: number }
    ) => {
      if (!lightingData) {
        setError("灯光数据未加载");
        return;
      }

      const newData = lightingData.map((rowData, r) =>
        rowData.map((led, c) => {
          if (r === row && c === col) {
            return {
              ...color,
              isCustom: true,
            };
          }
          return led;
        })
      );

      await updateLightingData(newData);
    },
    [lightingData, updateLightingData]
  );

  /**
   * 清空灯光
   */
  const clearLighting = useCallback(async () => {
    const emptyData: CustomLightingInfo[][] = Array(rows)
      .fill(null)
      .map(() =>
        Array(cols)
          .fill(null)
          .map(() => ({
            R: 0,
            G: 0,
            B: 0,
            isCustom: false,
          }))
      );

    await updateLightingData(emptyData);
  }, [rows, cols, updateLightingData]);

  /**
   * 本地更新（不发送到设备）
   */
  const setLocalLighting = useCallback((data: CustomLightingInfo[][]) => {
    setLightingData(data);
  }, []);

  // 初始加载
  useEffect(() => {
    if (enabled && actualSdk) {
      console.log("[useDecorativeLighting] 初始加载");
      if (solutionRovider !== "huafenda") {
        loadLightingData();
      }
    }
  }, [enabled, actualSdk, loadLightingData]);

  // 轮询：与 useKeyboardLighting 一致，上次回读完成后再 schedule 下一轮，避免 setInterval 堆叠 HID 请求
  useEffect(() => {
    if (pollingTimeoutRef.current !== null) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    if (!enabled || !actualSdk || pollingInterval <= 0) {
      return;
    }

    let cancelled = false;

    const scheduleNextPoll = () => {
      if (cancelled) return;
      pollingTimeoutRef.current = window.setTimeout(async () => {
        if (cancelled) return;
        if (solutionRoviderRef.current === "huafenda") {
          scheduleNextPoll();
          return;
        }
        if (isFetchingRef.current) {
          scheduleNextPoll();
          return;
        }
        isFetchingRef.current = true;
        try {
          await loadLightingDataRef.current({ silent: true });
        } catch {
          /* 超时或 SDK 异常：下一轮继续 */
        } finally {
          isFetchingRef.current = false;
          scheduleNextPoll();
        }
      }, pollingInterval) as unknown as number;
    };

    scheduleNextPoll();

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current !== null) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [enabled, actualSdk, pollingInterval]);

  const decorativeEnabled = Boolean(
    enabled && actualSdk && solutionRovider !== "huafenda"
  );

  useEffect(() => {
    if (typeof window === "undefined" || !decorativeEnabled) return;
    let wasLoading = false;
    const onLoadingChanged = (ev: Event) => {
      const loading = Boolean(
        (ev as CustomEvent<{ loading?: boolean }>).detail?.loading
      );
      if (wasLoading && !loading) {
        void loadLightingData();
      }
      wasLoading = loading;
    };
    window.addEventListener(
      OWNER_RESYNC_LOADING_CHANGED_EVENT,
      onLoadingChanged
    );
    return () => {
      window.removeEventListener(
        OWNER_RESYNC_LOADING_CHANGED_EVENT,
        onLoadingChanged
      );
    };
  }, [decorativeEnabled, loadLightingData]);

  return {
    lightingData,
    isLoading,
    error,
    loadLightingData,
    updateLightingData,
    updateSingleLed,
    clearLighting,
    setLocalLighting,
  };
}
